import { Box, IconButton, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { i18n } from '../../../../../../i18n/i18n';
import { COLORS } from '../../../../../../constants/constants';
import LabelsAndElements from '../../../../../../components/LabelsAndElements/LabelsAndElements';
import { formatDateOfBirth } from '../../../../../../utils/utils';
import CustomActions from '../../../../../../components/CustomActions/CustomActions';
import { IconAdd, IconChecked, IconCopy, IconImageSimilarity } from '../../../../../../images';
import { copyToClipboard } from '../../../../../../utils/communication';
import fallbackUserSquare from '../../../../../../images/fallbacks/user-fallback-square.svg';
import { Tooltip, SmartImage } from '@cellebrite/saas-ui-common';

interface MatchWideCardProps {
    findInput?: string;
    platform: any;
    account: any;
    onCheckedChange: any;
    setClickedAccount: any;
    showConfidence: boolean;
}
const MatchWideCard: React.FC<MatchWideCardProps> = ({
    findInput,
    platform,
    account,
    onCheckedChange,
    setClickedAccount,
    showConfidence,
}) => {
    const [cardActive, setCardActive] = useState(false);
    const [checked, setChecked] = useState(false);
    const ellipsisStyles = { textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' };
    const getRows = () => {
        const LABEL_MAP = {
            0: 'Date of birth',
            1: 'Username',
            2: 'Location',
            3: 'Email',
            4: 'Phone',
        };
        const dobs = account.overview?.dob
            ?.filter((dob) => dob.value?.start)
            .map((dob) => formatDateOfBirth(dob.value));
        const usernames = account.overview?.usernames
            ?.filter((username) => username.value?.userName)
            .map((username) => username.value.userName);
        const locations = account.overview?.locations
            ?.filter((location) => location.value)
            .map((location) => location.value?.address);
        const emails = account.overview?.emails?.filter((email) => email.value).map((email) => email.value);
        const phones = account.overview?.phones
            ?.filter((phone) => phone.value?.countryCode)
            .map((phone) => `${phone.value.countryCode}${phone.value.phone}`);
        const priorityData = [dobs, usernames, locations, emails, phones];
        const rows = [];
        priorityData.forEach((priorityPart, index) => {
            if (priorityPart.length && rows.length < 3) {
                rows.push({
                    label: (
                        <Tooltip
                            content={
                                <Box>
                                    {priorityPart.map((part, index) => (
                                        <Typography
                                            key={index}
                                            dangerouslySetInnerHTML={{
                                                __html: getMarkedText(part),
                                            }}
                                        ></Typography>
                                    ))}
                                </Box>
                            }
                        >
                            <Typography
                                sx={{ whiteSpace: 'nowrap' }}
                                variant="s12w500"
                                className="color-md"
                            >{`${i18n.translate(LABEL_MAP[index])} (${priorityPart.length})`}</Typography>
                        </Tooltip>
                    ),
                    labelSize: 12,
                    labelWeight: 500,
                    element: (
                        <Typography
                            variant="s12w500"
                            className="color-bd"
                            sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', mr: 1 }}
                            title={priorityPart[0]}
                            dangerouslySetInnerHTML={{
                                __html: getMarkedText(priorityPart[0]),
                            }}
                        ></Typography>
                    ),
                });
            }
        });
        return rows;
    };
    const regFindInput = findInput?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const getMarkedText = (text) => {
        let result = text;
        if (text && findInput?.length) {
            result = text.replaceAll(
                new RegExp(regFindInput, 'gi'),
                '<mark style="background-color: #FFECB2">$&</mark>',
            );
        }
        return result;
    };
    useEffect(() => {
        const updatedChecked = account.checked ? true : false;
        setChecked(updatedChecked);
    }, [account]);
    return (
        <Box
            className={`${
                account.added
                    ? 'border-color-p background-color-plst'
                    : checked
                      ? 'border-color-b background-color-blst'
                      : 'border-color-mho'
            } ${checked || account.added ? '' : 'background-color-hover-mho'}`}
            sx={{
                borderRadius: '8px',
                border: '1px solid',
                display: 'flex',
                minWidth: 406,
                maxWidth: 406,
                height: 154,
                position: 'relative',
            }}
            data-testid="matches-search-pipl-information"
            onClick={(e) => {
                if (!e['actionsClicked']) {
                    account.wideCard = true;
                    setClickedAccount(account);
                }
            }}
            onMouseEnter={() => setCardActive(true)}
            onMouseLeave={() => setCardActive(false)}
        >
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {!account.added && (cardActive || checked) && (
                    <Tooltip
                        placement="right"
                        content={<Typography>{i18n.translate(checked ? 'Selected' : 'Select')}</Typography>}
                    >
                        <Box
                            sx={{
                                'position': 'absolute',
                                'left': '0',
                                'm': 1,
                                'zIndex': 1,
                                'backgroundColor': checked ? COLORS.BRAND : COLORS.MUTED_DARK,
                                'borderRadius': '80px',
                                '&:hover': { backgroundColor: COLORS.MUTED_SHADY },
                            }}
                            onClick={(event) => {
                                event.stopPropagation();
                                setChecked(!checked);
                                if (onCheckedChange) {
                                    onCheckedChange(!checked, account);
                                }
                            }}
                        >
                            <IconButton sx={{ width: 24, height: 24 }}>
                                {checked ? <IconChecked fill="#FFF" /> : <IconAdd fill="#FFF" />}
                            </IconButton>
                        </Box>
                    </Tooltip>
                )}
                {account.isMatch && (
                    <Tooltip
                        placement="bottom-start"
                        content={
                            <Box>
                                <Typography variant="s14w500">{i18n.translate('AI facial similarity')}</Typography>
                                <Typography>
                                    {i18n.translate(
                                        'AI facial similarity suggests that this image is very similar to the search image',
                                    )}
                                </Typography>
                            </Box>
                        }
                    >
                        <Box
                            sx={{
                                backgroundColor: '#121E287A',
                                width: 24,
                                height: 24,
                                position: 'absolute',
                                top: 110,
                                m: 1,
                                zIndex: 1,
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <IconImageSimilarity />
                        </Box>
                    </Tooltip>
                )}
                {account.added && (
                    <Box
                        sx={{
                            position: 'absolute',
                            m: 1,
                            zIndex: 1,
                            width: 57,
                            height: 24,
                            backgroundColor: '#CBF5C8',
                            borderRadius: '4px',
                        }}
                    >
                        <Typography variant="s14w500" sx={{ ml: 1, mr: 1 }}>
                            {i18n.translate('Added')}
                        </Typography>
                    </Box>
                )}
                {account.matchScore >= 0 && !!showConfidence && (
                    <Box
                        sx={{
                            position: 'absolute',
                            left: `${account.isMatch ? 32 : 0}px`,
                            bottom: '0',
                            m: 1,
                            zIndex: 1,
                            backgroundColor: COLORS.WARNING_SOFT,
                            borderRadius: '4px',
                            px: 1,
                        }}
                    >
                        {' '}
                        <Typography variant="s14w500" color={COLORS.BRAND_DARK}>
                            {`${Math.round(account.matchScore * 100)}% ${i18n.translate('Confidence')}`}
                        </Typography>
                    </Box>
                )}
                <Box sx={{ display: 'flex' }}>
                    <SmartImage
                        fallbackImg={fallbackUserSquare}
                        src={account.image}
                        containerStyles={{
                            height: '152px',
                            width: '152px',
                            minWidth: '152px',
                            border: `${account.isMatch ? 3 : 0}px solid ${COLORS.WARNING_LIGHTEST}`,
                            borderTopLeftRadius: `${account.isMatch ? 4 : 8}px`,
                            borderBottomLeftRadius: `${account.isMatch ? 4 : 8}px`,
                        }}
                    />
                    <Box sx={{ ml: 2, mt: 2, overflow: 'hidden' }}>
                        <Typography
                            sx={ellipsisStyles}
                            variant="s16w700"
                            className="color-bd"
                            dangerouslySetInnerHTML={{
                                __html: getMarkedText(account.name),
                            }}
                        ></Typography>
                        <LabelsAndElements labelWidth={100} rows={getRows()} gap={0} />
                    </Box>
                </Box>
            </Box>
            {cardActive && (
                <CustomActions
                    containerStyles={{
                        'position': 'absolute',
                        'right': 10,
                        'top': 10,
                        '& .button-container': { backgroundColor: COLORS.MUTED_DARK, borderRadius: '80px' },
                        '& .button-container:hover': { backgroundColor: COLORS.MUTED_SHADY },
                        '& .button-container .icon': { fill: '#FFF' },
                    }}
                    actions={[
                        {
                            name: i18n.translate('Copy ID'),
                            icon: <IconCopy className="fill-md" />,
                            onClick: () => copyToClipboard(account.id),
                        },
                    ]}
                />
            )}
        </Box>
    );
};

export default MatchWideCard;
